import { useT } from "../../../shared/i18n/i18n";
import { Image } from "@tiptap/extension-image";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import type { NodeViewProps } from "@tiptap/react";
import { AlignCenter, AlignLeft, AlignRight, Download, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../../../shared/lib/cn";

// Resizable image node: side handles drag the width (center alignment grows
// both ways), a toolbar on selection switches alignment and offers
// download/delete. Width is stored in px; alignment rides on the wrapper as
// data-align so plain CSS does the layout.

const minImageWidth = 96;
const imageAligns = ["left", "center", "right"] as const;
type ImageAlign = (typeof imageAligns)[number];

const alignOptions: { icon: typeof AlignLeft; labelKey: string; value: ImageAlign }[] = [
  { icon: AlignLeft, labelKey: "editor.alignLeft", value: "left" },
  { icon: AlignCenter, labelKey: "editor.alignCenter", value: "center" },
  { icon: AlignRight, labelKey: "editor.alignRight", value: "right" },
];

function normalizeWidth(value: unknown): number | null {
  const parsed = typeof value === "number" ? value : typeof value === "string" ? Number.parseFloat(value) : Number.NaN;
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : null;
}

type ResizeSession = {
  handle: "left" | "right";
  maxWidth: number;
  pointerId: number;
  startClientX: number;
  startWidth: number;
};

function ImageNodeView({ deleteNode, editor, getPos, node, selected, updateAttributes }: NodeViewProps) {
  const t = useT();
  const align: ImageAlign = (imageAligns as readonly string[]).includes(node.attrs.align) ? node.attrs.align : "left";
  const committedWidth = normalizeWidth(node.attrs.width);
  const [width, setWidth] = useState<number | null>(committedWidth);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const sessionRef = useRef<ResizeSession | null>(null);

  useEffect(() => {
    if (!sessionRef.current) setWidth(committedWidth);
  }, [committedWidth]);

  const selectNode = useCallback(
    (withFocus: boolean) => {
      const pos = getPos();
      if (typeof pos !== "number") return;
      const chain = editor.chain();
      if (withFocus) chain.focus(undefined, { scrollIntoView: false });
      chain.setNodeSelection(pos).run();
    },
    [editor, getPos],
  );

  const columnWidth = () => containerRef.current?.parentElement?.clientWidth ?? Number.MAX_SAFE_INTEGER;

  const startResize = (handle: "left" | "right") => (event: React.PointerEvent<HTMLDivElement>) => {
    if (!editor.isEditable || sessionRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    const container = containerRef.current;
    if (!container) return;
    sessionRef.current = {
      handle,
      maxWidth: columnWidth(),
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startWidth: container.clientWidth,
    };
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Pointer capture is best-effort: move/up still reach the handle.
    }
    setIsResizing(true);
    selectNode(false);
  };

  const moveResize = (event: React.PointerEvent<HTMLDivElement>) => {
    const session = sessionRef.current;
    if (!session || event.pointerId !== session.pointerId) return;
    const raw = event.clientX - session.startClientX;
    const directional = session.handle === "left" ? -raw : raw;
    const delta = align === "center" ? directional * 2 : directional;
    setWidth(Math.min(Math.max(Math.round(session.startWidth + delta), minImageWidth), session.maxWidth));
  };

  const endResize = (event: React.PointerEvent<HTMLDivElement>) => {
    const session = sessionRef.current;
    if (!session || event.pointerId !== session.pointerId) return;
    sessionRef.current = null;
    setIsResizing(false);
    const finalWidth = containerRef.current ? Math.round(containerRef.current.getBoundingClientRect().width) : width;
    if (finalWidth && finalWidth !== committedWidth) updateAttributes({ width: finalWidth });
    selectNode(true);
  };

  const keyboardResize = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!editor.isEditable) return;
    if (event.key === "Escape") {
      // Swallow it: Escape on a handle returns focus to the editor and must
      // not bubble up to the surrounding dialog and close it.
      event.preventDefault();
      event.stopPropagation();
      editor.commands.focus();
      return;
    }
    const step = event.shiftKey ? 64 : 16;
    const delta =
      event.key === "ArrowRight" || event.key === "ArrowUp"
        ? step
        : event.key === "ArrowLeft" || event.key === "ArrowDown"
          ? -step
          : 0;
    if (delta === 0) return;
    event.preventDefault();
    const current = width ?? (containerRef.current ? Math.round(containerRef.current.clientWidth) : minImageWidth);
    const next = Math.min(Math.max(current + delta, minImageWidth), columnWidth());
    setWidth(next);
    updateAttributes({ width: next });
  };

  const handleImageClick = (event: React.MouseEvent) => {
    if (!editor.isEditable || sessionRef.current) return;
    event.preventDefault();
    selectNode(true);
  };

  const downloadImage = () => {
    const link = document.createElement("a");
    link.href = node.attrs.src;
    link.download = typeof node.attrs.alt === "string" && node.attrs.alt.length > 0 ? node.attrs.alt : "image";
    document.body.append(link);
    link.click();
    link.remove();
  };

  const currentWidth = width ?? committedWidth ?? undefined;
  const showControls = editor.isEditable;

  const resizeHandle = (handle: "left" | "right") => (
    <div
      aria-label={handle === "left" ? t("docTable.resizeLeft") : t("docTable.resizeRight")}
      aria-orientation="horizontal"
      aria-valuemax={Math.round(columnWidth())}
      aria-valuemin={minImageWidth}
      aria-valuenow={currentWidth ?? minImageWidth}
      className={cn("document-image__handle", handle === "left" ? "document-image__handle--left" : "document-image__handle--right")}
      contentEditable={false}
      data-editor-chrome=""
      onKeyDown={keyboardResize}
      onPointerCancel={endResize}
      onPointerDown={startResize(handle)}
      onPointerMove={moveResize}
      onPointerUp={endResize}
      role="slider"
      tabIndex={selected ? 0 : -1}
    />
  );

  return (
    <NodeViewWrapper
      className="document-image"
      data-align={align}
      data-resizing={isResizing ? "true" : undefined}
      data-selected={selected ? "true" : undefined}
    >
      <div className="document-image__container" ref={containerRef} style={{ width: currentWidth ? `${currentWidth}px` : undefined }}>
        <img
          alt={typeof node.attrs.alt === "string" ? node.attrs.alt : ""}
          draggable={false}
          onClick={handleImageClick}
          src={node.attrs.src}
        />
        {showControls ? (
          <>
            {resizeHandle("left")}
            {resizeHandle("right")}
            {selected && !isResizing ? (
              <div
                aria-label={t("editor.imageOptions")}
                className="absolute left-1/2 top-2 z-20 flex -translate-x-1/2 items-center gap-0.5 whitespace-nowrap rounded-md border border-border bg-background/95 p-0.5 shadow-sm backdrop-blur"
                contentEditable={false}
                data-editor-chrome=""
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    event.stopPropagation();
                    editor.commands.focus();
                  }
                }}
                onPointerDown={(event) => event.stopPropagation()}
                role="toolbar"
              >
                {alignOptions.map((option) => (
                  <button
                    aria-label={t(option.labelKey)}
                    aria-pressed={align === option.value}
                    className={cn(
                      "flex size-7 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring",
                      align === option.value && "bg-accent text-foreground",
                    )}
                    key={option.value}
                    onClick={() => {
                      updateAttributes({ align: option.value });
                      selectNode(true);
                    }}
                    type="button"
                  >
                    <option.icon aria-hidden className="size-4" />
                  </button>
                ))}
                <button
                  aria-label={t("editor.downloadImage")}
                  className="flex size-7 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
                  onClick={downloadImage}
                  type="button"
                >
                  <Download aria-hidden className="size-4" />
                </button>
                <button
                  aria-label={t("editor.deleteImage")}
                  className="flex size-7 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-2 focus-visible:outline-ring"
                  onClick={() => deleteNode()}
                  type="button"
                >
                  <Trash2 aria-hidden className="size-4" />
                </button>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </NodeViewWrapper>
  );
}

export const DocumentImage = Image.extend({
  draggable: false,

  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        default: "left",
        parseHTML: (element) => {
          const value = element.getAttribute("data-align");
          return value && (imageAligns as readonly string[]).includes(value) ? value : "left";
        },
        renderHTML: (attributes) =>
          attributes.align === "center" || attributes.align === "right" ? { "data-align": attributes.align } : {},
      },
      width: {
        default: null,
        parseHTML: (element) => normalizeWidth(element.getAttribute("width")),
        renderHTML: (attributes) => {
          const value = normalizeWidth(attributes.width);
          return value ? { width: String(value) } : {};
        },
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView);
  },
});
